import type { PumpStatus, ReservoirStatus, ScadaDevice, StatusSeverity } from '../api/scada'

// Shared device-status classifier -- originally lived in ScadaView.tsx for
// the map's pulsing markers, extracted here so DemandsAttentionSection.tsx's
// tiles use the exact same classification instead of a second, independently
// drifting one (the whole point of retiring the old critical/warning tile
// model: "eliminates visual desyncs where a tile claims a node is healthy
// while the map pulse signals an alarm").
//
// Returns a category, not a resolved color -- callers derive the actual
// color via CATEGORY_COLOR[category] themselves (the map's marker fill,
// DemandsAttentionSection's icon fill, its WCAG-safe text variant, etc. all
// need slightly different renderings of "the same category," so the
// category is the shared fact, not one specific color).
export type PulseCategory = 'positive' | 'problem' | 'disconnected'

export const CATEGORY_COLOR: Record<PulseCategory, string> = {
  positive:     'var(--color-brand-cyan)',
  problem:      'var(--color-brand-critical)',
  disconnected: 'var(--color-brand-gray)',
}

// WCAG-safe text variant of the same three tones — same reasoning as
// ScadaView.tsx's ZONE_STATUS_TEXT_COLOR (the fill colors above read fine
// on a map surface but fail contrast as small popup/label text).
export const CATEGORY_TEXT_COLOR: Record<PulseCategory, string> = {
  positive:     'var(--color-brand-cyan-text)',
  problem:      'var(--color-brand-critical-text)',
  disconnected: 'var(--color-brand-gray-text)',
}

// Resolves a Pulse to an actual color, honoring severity -- CATEGORY_COLOR/
// CATEGORY_TEXT_COLOR alone only have one tone for 'problem' (critical red),
// so a warning-severity pulse and a critical one were rendering identically.
// Branding guide §3b/§4A: warning = --color-brand-warning (Problem Orange),
// critical = --color-brand-critical (the existing red). severity absent on a
// 'problem' pulse falls back to critical, matching current behavior for any
// case that doesn't set it.
export function pulseColor(p: Pick<Pulse, 'category' | 'severity'>): string {
  if (p.category === 'problem' && p.severity === 'warning') return 'var(--color-brand-warning)'
  return CATEGORY_COLOR[p.category]
}
export function pulseTextColor(p: Pick<Pulse, 'category' | 'severity'>): string {
  if (p.category === 'problem' && p.severity === 'warning') return 'var(--color-brand-warning-text)'
  return CATEGORY_TEXT_COLOR[p.category]
}

export function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

// Placeholder, not calibrated against real data -- there's no historical
// fill/drain-rate figure to check "fast" against yet (routed to overwatch
// to get real numbers from SCADA/ops). 0.3 m/h reads as "fast" (near-max
// pulse speed) until corrected; trivial to retune once a real value exists.
const FAST_RATE_M_PER_H = 0.3
function rateIntensity(rateMPerH: number): number {
  return clamp01(Math.abs(rateMPerH) / FAST_RATE_M_PER_H)
}

// severity is set only when category is 'problem' -- it's the map's cue for
// whether a marker should animate (deviceShouldPulse-equivalent, see
// ScadaView.tsx's objectMarkerIcon `pulsing` prop): critical pulses, warning
// stays static so a screen of orange doesn't dilute the one red thing on it.
// Category alone can't carry that distinction -- CATEGORY_COLOR only has one
// tone for 'problem', warning and critical share it.
export type Pulse = { category: PulseCategory; intensity: number; severity?: 'warning' | 'critical' }

// Category/severity come from the backend's status_severity (services/
// alerts.py device_statuses: 'unknown' = no signal, 'critical'/'warning' =
// an open alert episode, 'ok' = fine) -- web no longer keeps its own
// staleness cutoff or reservoir/pump thresholds. Only intensity (pulse
// speed) is still computed locally, from the reading itself.
function severityPulse(sev: StatusSeverity | null | undefined, base: number): Pulse {
  if (sev === 'critical') return { category: 'problem', intensity: Math.max(base, 1), severity: 'critical' }
  if (sev === 'warning')  return { category: 'problem', intensity: Math.max(base, 0.5), severity: 'warning' }
  if (sev === 'unknown')  return { category: 'disconnected', intensity: 0.2 }
  if (sev === 'ok')       return { category: 'positive', intensity: base }
  return { category: 'disconnected', intensity: 0.1 } // inactive (null) or no status row at all
}

export function reservoirPulse(r: ReservoirStatus | undefined): Pulse {
  if (!r) return { category: 'disconnected', intensity: 0.1 }
  return severityPulse(r.status_severity, rateIntensity(r.rate_m_per_h ?? 0))
}

// A stopped pump is intentionally-off (intensity 0) when severity is ok --
// only an open alert episode turns it into a problem, never gray.
export function pumpPulse(p: PumpStatus | undefined): Pulse {
  if (!p) return { category: 'disconnected', intensity: 0.1 }
  return severityPulse(p.status_severity, p.status === 'running' ? 0.15 : 0)
}

// Every node type has status/status_severity now; pump/reservoir still read
// their own /latest row (fresher than the /devices list), the rest use the
// device row's own fields.
export function devicePulse(d: ScadaDevice, pumpByKey: Map<string, PumpStatus>, reservoirByKey: Map<string, ReservoirStatus>): Pulse {
  if (d.node_type === 'pump')      return pumpPulse(pumpByKey.get(d.device_key))
  if (d.node_type === 'reservoir') return reservoirPulse(reservoirByKey.get(d.device_key))
  return severityPulse(d.status_severity, 0.15)
}
